import { useMemo } from 'react'
import { Empty } from '@cloudflare/kumo'
import { CalendarBlankIcon } from '@phosphor-icons/react'
import type { GuestInvitationDto, RoomBookingDto } from '@hub-wifi/shared'
import { useTranslation } from '../../i18n'
import { buildAgenda } from '../home/agenda'
import { DayGroup } from '../home/HomeAgenda'
import '../home/home.css'

export interface UpcomingInvitationsProps {
  invitations: GuestInvitationDto[]
  bookings: RoomBookingDto[]
  timeZone: string
  now: Date
}

/**
 * Upcoming invitations as cards grouped by day (the home agenda's grouping and
 * rows): guests of a booking nest under it, the rest are standalone rows.
 * Only bookings that have guests are listed here.
 */
export function UpcomingInvitations({
  invitations,
  bookings,
  timeZone,
  now,
}: UpcomingInvitationsProps) {
  const { t } = useTranslation()
  const agenda = useMemo(() => {
    const withGuests = bookings.filter((b) => invitations.some((i) => i.roomBookingId === b.id))
    return buildAgenda(withGuests, invitations, timeZone, now, { limit: Infinity })
  }, [invitations, bookings, timeZone, now])

  if (agenda.days.length === 0) {
    return (
      <Empty
        size="sm"
        icon={<CalendarBlankIcon size={32} className="text-kumo-inactive" />}
        title={t('guestInvitations.list.empty')}
        description={t('guestInvitations.list.emptyDescription')}
      />
    )
  }
  return (
    <div className="flex flex-col gap-4">
      {agenda.days.map((day) => (
        <DayGroup key={day.ymd} day={day} />
      ))}
    </div>
  )
}
